import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';

const VIEWS = ['calendar', 'timeline', 'gaps'];
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const CATEGORY_KEYS = ['polish_national', 'eu', 'un', 'sector', 'religious', 'cultural', 'custom'];

// Fallback used when the Worker is unreachable in dev. Same shape as the API.
const MOCK_DATES = [
  { id: 'mock-1',  month: 6, day: 1,  namePl: 'Dzień Dziecka',                       nameEn: "Children's Day",                category: 'polish_national', topic: 'youth' },
  { id: 'mock-2',  month: 6, day: 5,  namePl: 'Światowy Dzień Środowiska',           nameEn: 'World Environment Day',         category: 'un',              topic: 'sustainability' },
  { id: 'mock-3',  month: 6, day: 8,  namePl: 'Światowy Dzień Oceanów',              nameEn: 'World Oceans Day',              category: 'un',              topic: 'sustainability' },
  { id: 'mock-4',  month: 6, day: 12, namePl: 'Heritage Summit Kraków',              nameEn: 'Heritage Summit Kraków',        category: 'custom',          topic: 'heritage' },
  { id: 'mock-5',  month: 6, day: 20, namePl: 'Światowy Dzień Uchodźcy',             nameEn: 'World Refugee Day',             category: 'un',              topic: 'partnership' },
  { id: 'mock-6',  month: 6, day: 21, namePl: 'Święto Muzyki',                       nameEn: 'World Music Day',               category: 'cultural',        topic: 'culture' },
];

// Bilingual content suggestions keyed by topic (the DB has no per-date suggestion
// column — these are AI-generated angles that the future generation API would
// populate. Topic-keyed gives us reasonable defaults until then).
const SUGGESTIONS_BY_TOPIC = {
  heritage:       { en: 'Spotlight a heritage site or restoration story. Use #HeritageProtection.',
                    pl: 'Pokaż obiekt dziedzictwa lub historię renowacji. Użyj #OchronaDziedzictwa.' },
  sustainability: { en: 'Frame heritage protection as environmental protection — old buildings are sustainable buildings.',
                    pl: 'Pokaż ochronę dziedzictwa jako ochronę środowiska — stare budynki są zrównoważone.' },
  youth:          { en: 'Spotlight a youth-led heritage project — celebrate the next generation of stewards.',
                    pl: 'Wyróżnij projekt dziedzictwa prowadzony przez młodzież — świętujmy nowe pokolenie opiekunów.' },
  partnership:    { en: 'Thank a partner organisation publicly — name them, tag them.',
                    pl: 'Publicznie podziękuj organizacji partnerskiej — wymień ją z nazwy, oznacz.' },
  culture:        { en: 'Highlight a cultural tradition tied to F8M\'s mission. Pull a quote from a partner.',
                    pl: 'Wyróżnij tradycję kulturową związaną z misją F8M. Zacytuj partnera.' },
  diplomacy:      { en: 'Profile a public servant or institution working in cultural policy. Tag them.',
                    pl: 'Przedstaw urzędnika lub instytucję działającą w polityce kulturalnej. Oznacz.' },
  education:      { en: 'Educational angle — what F8M teaches, what we\'re learning from communities.',
                    pl: 'Aspekt edukacyjny — czego uczy F8M, czego my uczymy się od społeczności.' },
};

// Topic coverage per month for the Gaps view (true = has content, false = gap).
// Hardcoded so it doesn't change between renders.
const GAP_COVERAGE = {
  heritage:       [true,  true,  false, false, true,  true,  false, false, true,  true,  true,  false],
  sustainability: [false, true,  false, true,  false, true,  true,  false, true,  false, false, true],
  youth:          [false, false, true,  false, true,  false, false, true,  false, true,  true,  false],
  partnership:    [true,  false, true,  true,  false, true,  false, false, true,  true,  false, false],
};

const GAP_TOPICS = ['heritage', 'sustainability', 'youth', 'partnership'];

// Helpers ──────────────────────────────────────────────────────────────────
function buildMonthGrid(year, monthIndex /* 0-11 */) {
  const firstOfMonth = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  // Convert to Mon=0..Sun=6
  const offset = (firstOfMonth.getDay() + 6) % 7;
  const cells = [];
  // previous-month padding
  const daysInPrev = new Date(year, monthIndex, 0).getDate();
  for (let i = offset - 1; i >= 0; i -= 1) {
    cells.push({ day: daysInPrev - i, inMonth: false });
  }
  // current month
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ day, inMonth: true });
  }
  // next-month padding to fill 6 weeks (42 cells)
  while (cells.length < 42) {
    cells.push({ day: cells.length - daysInMonth - offset + 1, inMonth: false });
  }
  return cells;
}

function isToday(year, monthIndex, day) {
  const now = new Date();
  return (
    now.getFullYear() === year &&
    now.getMonth() === monthIndex &&
    now.getDate() === day
  );
}

function formatMonthYear(year, monthIndex, language) {
  const locale = language === 'pl' ? 'pl-PL' : 'en-GB';
  return new Date(year, monthIndex, 1).toLocaleString(locale, {
    month: 'long',
    year: 'numeric',
  });
}

function formatLongDate(year, monthIndex, day, language) {
  const locale = language === 'pl' ? 'pl-PL' : 'en-GB';
  return new Date(year, monthIndex, day).toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// Calendar view ────────────────────────────────────────────────────────────
function CalendarView({ datesByDay, year, monthIndex, onPrev, onNext, selected, onSelect }) {
  const { t, language } = useTranslation();
  const cells = useMemo(() => buildMonthGrid(year, monthIndex), [year, monthIndex]);

  return (
    <div>
      <div className="calendar-toolbar">
        <div className="month-nav">
          <button type="button" className="month-nav__button" onClick={onPrev} aria-label={t('calendar.prevMonth')}>
            ◀
          </button>
          <span className="month-nav__title">{formatMonthYear(year, monthIndex, language)}</span>
          <button type="button" className="month-nav__button" onClick={onNext} aria-label={t('calendar.nextMonth')}>
            ▶
          </button>
        </div>
      </div>

      <div className="calendar-legend">
        {CATEGORY_KEYS.map((cat) => (
          <span key={cat} className="calendar-legend__item">
            <span className={`calendar-legend__dot dot--${cat}`} aria-hidden="true" />
            {t(`calendar.categories.${cat}`)}
          </span>
        ))}
      </div>

      <div className="calendar-layout">
        <div className="calendar-month-card">
          <div className="calendar-month">
            {WEEKDAY_KEYS.map((wd) => (
              <div key={wd} className="calendar-month__weekday">{t(`calendar.weekdays.${wd}`)}</div>
            ))}
            {cells.map((cell, i) => {
              const dates = cell.inMonth ? datesByDay[cell.day] || [] : [];
              const today = cell.inMonth && isToday(year, monthIndex, cell.day);
              const isSelected = cell.inMonth && selected === cell.day;
              const className = [
                'day-cell',
                cell.inMonth ? '' : 'day-cell--out',
                today ? 'day-cell--today' : '',
                isSelected ? 'day-cell--selected' : '',
              ].filter(Boolean).join(' ');

              if (!cell.inMonth) {
                return (
                  <div key={`out-${i}`} className={className}>
                    <span className="day-cell__number">{cell.day}</span>
                  </div>
                );
              }

              return (
                <button
                  key={`d-${cell.day}`}
                  type="button"
                  className={className}
                  onClick={() => dates.length && onSelect(cell.day)}
                  aria-label={`${cell.day} ${formatMonthYear(year, monthIndex, language)}`}
                >
                  <span className="day-cell__number">{cell.day}</span>
                  {dates.length > 0 && (
                    <>
                      <span className="day-cell__dots">
                        {dates.map((d, idx) => (
                          <span key={idx} className={`day-cell__dot dot--${d.category}`} aria-hidden="true" />
                        ))}
                      </span>
                      <span className="day-cell__name">
                        {dates.length === 1 ? (language === 'pl' ? dates[0].namePl : dates[0].nameEn) : `${dates.length} dates`}
                      </span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <DayDetailPanel
          year={year}
          monthIndex={monthIndex}
          day={selected}
          dates={selected ? datesByDay[selected] || [] : []}
        />
      </div>
    </div>
  );
}

function DayDetailPanel({ year, monthIndex, day, dates }) {
  const { t, language } = useTranslation();

  if (!day) {
    return (
      <aside className="day-detail">
        <div className="day-detail__placeholder">{t('calendar.panel.selectADay')}</div>
      </aside>
    );
  }

  return (
    <aside className="day-detail">
      <div className="day-detail__date">{formatLongDate(year, monthIndex, day, language)}</div>
      {dates.map((d) => {
        const suggestion = d.topic ? SUGGESTIONS_BY_TOPIC[d.topic]?.[language] ?? '' : '';
        const topicLabel = d.topic ? t(`calendar.topics.${d.topic}`) : null;
        return (
          <div key={d.id} className="day-detail__entry">
            <div className="day-detail__name-pl">
              {language === 'pl' ? d.namePl : (d.nameEn || d.namePl)}
            </div>
            {language === 'pl' && d.nameEn && (
              <div className="day-detail__name-en">{d.nameEn}</div>
            )}
            {language === 'en' && d.namePl && d.namePl !== d.nameEn && (
              <div className="day-detail__name-en">{d.namePl}</div>
            )}
            <div className="day-detail__meta">
              <span className={`cat-badge cat-badge--${d.category}`}>
                {t(`calendar.categoryEmoji.${d.category}`)} {t(`calendar.categories.${d.category}`)}
              </span>
              {topicLabel && (
                <span className="day-detail__topic">
                  {t('calendar.panel.relatedTopic')}: {topicLabel}
                </span>
              )}
            </div>
            {suggestion && (
              <>
                <span className="day-detail__suggestion-label">
                  {t('calendar.panel.contentSuggestion')}
                </span>
                <p className="day-detail__suggestion">{suggestion}</p>
              </>
            )}
          </div>
        );
      })}
      <button type="button" className="button button--primary">
        {t('calendar.panel.generatePosts')}
      </button>
    </aside>
  );
}

// Timeline view ────────────────────────────────────────────────────────────
function TimelineView() {
  const { t } = useTranslation();
  return (
    <div className="timeline-placeholder">
      <div className="coming-soon__icon" aria-hidden="true">📊</div>
      <h2>{t('calendar.timeline.comingSoon')}</h2>
      <p>{t('calendar.timeline.description')}</p>
      <div className="timeline-placeholder__chart" aria-hidden="true">
        {[40, 65, 30, 80, 55, 90, 45, 70, 60, 35, 50, 75].map((h, i) => (
          <div key={i} className="timeline-placeholder__bar" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

// Gaps view ────────────────────────────────────────────────────────────────
function GapsView({ year }) {
  const { t, language } = useTranslation();
  const monthLabels = useMemo(() => {
    const locale = language === 'pl' ? 'pl-PL' : 'en-GB';
    return Array.from({ length: 12 }, (_, m) =>
      new Date(year, m, 1).toLocaleString(locale, { month: 'short' }),
    );
  }, [year, language]);

  return (
    <div className="gaps-card">
      <div className="gaps-card__header">
        <h2 className="gaps-card__title">{t('calendar.gaps.title')}</h2>
        <p className="gaps-card__subtitle">{t('calendar.gaps.subtitle')}</p>
      </div>

      <div className="gaps-row gaps-row--header">
        <span className="gaps-row__topic">{t('calendar.gaps.topicHeader')}</span>
        {monthLabels.map((label) => (
          <span key={label} className="gaps-row__month-label">{label}</span>
        ))}
      </div>

      {GAP_TOPICS.map((topic) => (
        <div key={topic} className="gaps-row">
          <span className="gaps-row__topic">{t(`calendar.topics.${topic}`)}</span>
          {GAP_COVERAGE[topic].map((filled, monthIdx) => (
            <div
              key={monthIdx}
              className={'gaps-cell ' + (filled ? 'gaps-cell--filled' : 'gaps-cell--gap')}
              tabIndex={filled ? -1 : 0}
              aria-label={
                filled
                  ? `${t(`calendar.topics.${topic}`)} — ${monthLabels[monthIdx]}: planned`
                  : t('calendar.gaps.tooltip', { topic: t(`calendar.topics.${topic}`), month: monthLabels[monthIdx] })
              }
            >
              {filled ? '✓' : '!'}
              {!filled && (
                <div className="gaps-cell__tip" role="tooltip">
                  {t('calendar.gaps.tooltip', { topic: t(`calendar.topics.${topic}`), month: monthLabels[monthIdx] })}{' '}
                  {t(`calendar.gaps.tips.${topic}`)}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// Main component ───────────────────────────────────────────────────────────
export function CalendarPage() {
  const { t } = useTranslation();
  const [view, setView] = useState('calendar');
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [monthIndex, setMonthIndex] = useState(() => new Date().getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [dates, setDates] = useState([]);
  const [usingMockData, setUsingMockData] = useState(false);

  // Fetch all calendar dates once. They're a small bounded set (~36 rows seeded),
  // so a single fetch covers every month — month-switching is purely client-side.
  useEffect(() => {
    let cancelled = false;
    api.calendarDates
      .list()
      .then((data) => {
        if (cancelled) return;
        setDates(Array.isArray(data?.dates) ? data.dates : []);
        setUsingMockData(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[calendar] API unreachable, using mock dates:', err.message);
        setDates(MOCK_DATES);
        setUsingMockData(true);
      });
    return () => { cancelled = true; };
  }, []);

  const datesByDay = useMemo(() => {
    const monthNumber = monthIndex + 1;
    const map = {};
    for (const d of dates) {
      if (d.month !== monthNumber) continue;
      (map[d.day] ||= []).push(d);
    }
    return map;
  }, [dates, monthIndex]);

  function navigate(delta) {
    let m = monthIndex + delta;
    let y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonthIndex(m);
    setYear(y);
    setSelectedDay(null);
  }

  return (
    <div className="calendar-page">
      <header className="page-header">
        <h1 className="page-header__title">{t('calendar.title')}</h1>
        <div className="page-header__subtitle">{t('calendar.subtitle')}</div>
      </header>

      {usingMockData && <div className="dev-notice">{t('calendar.loadError')}</div>}

      <div className="filter-pills" role="tablist" aria-label={t('calendar.title')}>
        {VIEWS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className={'filter-pill' + (view === key ? ' filter-pill--active' : '')}
            onClick={() => setView(key)}
          >
            {t(`calendar.views.${key}`)}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 'var(--space-5)' }}>
        {view === 'calendar' && (
          <CalendarView
            datesByDay={datesByDay}
            year={year}
            monthIndex={monthIndex}
            onPrev={() => navigate(-1)}
            onNext={() => navigate(1)}
            selected={selectedDay}
            onSelect={setSelectedDay}
          />
        )}
        {view === 'timeline' && <TimelineView />}
        {view === 'gaps' && <GapsView year={year} />}
      </div>
    </div>
  );
}
